<template>
    <div class="ride-indicators-page">
        <!-- Header com filtros -->
        <div class="metrics-header-card">
            <div class="card-header d-flex justify-content-between align-items-center">
                <div>
                    <h4 class="metrics-title mb-1">
                        <i class="fas fa-users mr-2"></i>
                        Indicadores de Carona
                    </h4>
                    <p class="metrics-subtitle mb-0">Acompanhe o compartilhamento de viagens na sua organização</p>
                </div>
                <div class="header-actions">
                    <button
                        class="btn btn-outline-primary btn-action"
                        data-toggle="collapse"
                        data-target="#filterRideMetrics"
                        aria-expanded="false"
                        aria-controls="filterRideMetrics"
                    >
                        <i class="fas fa-filter"></i>
                        Filtros
                    </button>
                </div>
            </div>

            <!-- Filtros colapsáveis -->
            <div
                class="collapse"
                id="filterRideMetrics"
            >
                <div class="filters-section">
                    <div class="row">
                        <div class="col-md-3">
                            <div class="form-group">
                                <label for="branch">Filial</label>
                                <v-select
                                    id="branch"
                                    :options="branches"
                                    label="description"
                                    :reduce="(branch) => branch.id"
                                    v-model="filters.branch_id"
                                    placeholder="Todas as Filiais"
                                    :clearable="true"
                                    class="style-chooser"
                                >
                                    <div slot="no-options">Nenhuma filial encontrada</div>
                                </v-select>
                            </div>
                        </div>

                        <div class="col-md-3">
                            <div class="form-group">
                                <label for="costCenter">Centro de Custo</label>
                                <v-select
                                    id="costCenter"
                                    :options="costCenters"
                                    label="erp_code"
                                    :reduce="(costCenter) => costCenter.id"
                                    v-model="filters.cost_center_id"
                                    placeholder="Todos os Centros de Custo"
                                    :clearable="true"
                                    class="style-chooser"
                                >
                                    <div slot="no-options">Nenhum centro de custo encontrado</div>
                                </v-select>
                            </div>
                        </div>

                        <div class="col-md-3">
                            <div class="form-group">
                                <label for="startDate">Data Início</label>
                                <input
                                    type="date"
                                    id="startDate"
                                    v-model="filters.start_date"
                                    class="form-control"
                                >
                            </div>
                        </div>

                        <div class="col-md-3">
                            <div class="form-group">
                                <label for="endDate">Data Fim</label>
                                <input
                                    type="date"
                                    id="endDate"
                                    v-model="filters.end_date"
                                    class="form-control"
                                >
                            </div>
                        </div>

                        <div class="col-md-12">
                            <div class="d-flex gap-2 justify-content-end">
                                <button
                                    class="btn btn-secondary"
                                    @click="resetFilters"
                                >
                                    <i class="fas fa-eraser mr-1"></i>
                                    Limpar
                                </button>

                                <button
                                    class="btn btn-primary"
                                    @click="applyFilters"
                                >
                                    <i class="fas fa-search mr-1"></i>
                                    Filtrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            <div class="p-4">
                <!-- Loading state -->
                <div
                    v-if="loading"
                    class="loading-container"
                >
                    <div
                        class="spinner-border text-primary"
                        role="status"
                    >
                        <span class="sr-only">Carregando...</span>
                    </div>
                    <p class="mt-2 text-muted">Carregando métricas...</p>
                </div>

                <!-- Error state -->
                <div
                    v-else-if="error"
                    class="alert alert-danger"
                    role="alert"
                >
                    <i class="fas fa-exclamation-triangle mr-2"></i>
                    {{ error }}
                </div>

                <!-- Métricas -->
                <div
                    v-else
                    class="metrics-grid"
                >
                    <!-- Cards principais -->
                    <div class="main-metrics-row">
                        <!-- Caronas Solicitadas -->
                        <div class="metric-card rides-requested">
                            <div class="card-icon">
                                <i class="fas fa-hand-paper"></i>
                            </div>
                            <div class="card-content">
                                <div class="card-value">{{ metrics.total_rides_requested || 0 }}</div>
                                <div class="card-label">Caronas Solicitadas</div>
                            </div>
                        </div>

                        <!-- Caronas Aprovadas -->
                        <div class="metric-card rides-approved">
                            <div class="card-icon">
                                <i class="fas fa-check-circle"></i>
                            </div>
                            <div class="card-content">
                                <div class="card-value">{{ metrics.total_rides_approved || 0 }}</div>
                                <div class="card-label">Caronas Aprovadas</div>
                            </div>
                        </div>

                        <!-- Caronas Reprovadas -->
                        <div class="metric-card rides-rejected">
                            <div class="card-icon">
                                <i class="fas fa-times-circle"></i>
                            </div>
                            <div class="card-content">
                                <div class="card-value">{{ metrics.total_rides_rejected || 0 }}</div>
                                <div class="card-label">Caronas Reprovadas</div>
                            </div>
                        </div>

                        <!-- Caronas Pendentes -->
                        <div class="metric-card rides-pending">
                            <div class="card-icon">
                                <i class="fas fa-clock"></i>
                            </div>
                            <div class="card-content">
                                <div class="card-value">{{ metrics.total_rides_pending || 0 }}</div>
                                <div class="card-label">Caronas Pendentes</div>
                            </div>
                        </div>

                        <!-- Total de Reservas Compartilhadas -->
                        <div class="metric-card shared-reservations">
                            <div class="card-icon">
                                <i class="fas fa-share-alt"></i>
                            </div>
                            <div class="card-content">
                                <div class="card-value">{{ metrics.total_shared_reservations || 0 }}</div>
                                <div class="card-label">Reservas Compartilhadas</div>
                            </div>
                        </div>

                        <!-- Caronas Diretas -->
                        <div class="metric-card rides-automatic">
                            <div class="card-icon">
                                <i class="fas fa-user-plus"></i>
                            </div>
                            <div class="card-content">
                                <div class="card-value">{{ metrics.total_rides_automatic || 0 }}</div>
                                <div class="card-label">Caronas Diretas</div>
                            </div>
                        </div>
                    </div>

                    <!-- Seção de compartilhamento -->
                    <div class="sharing-section">

                        <!-- Top Centros de Custo -->
                        <div class="top-cost-centers-card">
                            <div class="card-header">
                                <h5>
                                    <i class="fas fa-trophy mr-2"></i>
                                    Top Centros de Custo - Compartilhamento
                                </h5>
                            </div>
                            <div class="card-body">
                                <div
                                    v-if="Object.keys(metrics.top_sharing_cost_centers || {}).length === 0"
                                    class="no-data"
                                >
                                    <i class="fas fa-info-circle mr-2"></i>
                                    Nenhum compartilhamento registrado
                                </div>
                                <div
                                    v-else
                                    class="cost-centers-list"
                                >
                                    <div
                                        v-for="(count, costCenter, index) in metrics.top_sharing_cost_centers"
                                        :key="costCenter"
                                        class="cost-center-item"
                                        :class="{
                                    'first-place': index === 0,
                                    'second-place': index === 1,
                                    'third-place': index === 2
                                }"
                                    >
                                        <div class="ranking-position">
                                            <i
                                                v-if="index === 0"
                                                class="fas fa-crown text-warning"
                                            ></i>
                                            <i
                                                v-else-if="index === 1"
                                                class="fas fa-medal text-secondary"
                                            ></i>
                                            <i
                                                v-else-if="index === 2"
                                                class="fas fa-award text-warning"
                                            ></i>
                                            <span
                                                v-else
                                                class="position-number"
                                            >{{ index + 1 }}º
                                            </span>
                                        </div>
                                        <div class="cost-center-info">
                                            <div class="cost-center-name">{{ costCenter }}</div>
                                            <div class="cost-center-count">{{ count }} viagens compartilhadas</div>
                                        </div>
                                        <div class="progress-bar">
                                            <div
                                                class="progress-fill"
                                                :style="{ width: calculatePercentage(count) + '%' }"
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Taxa de aprovação (se houver dados) -->
                        <div
                            v-if="!loading && !error && metrics.total_rides_requested > 0"
                            class="approval-rate-section"
                        >
                            <div class="approval-rate-card">
                                <div class="approval-rate-header">
                                    <h5>
                                        <i class="fas fa-chart-pie mr-2"></i>
                                        Taxa de Aprovação de Caronas
                                    </h5>
                                </div>
                                <div class="approval-rate-content">
                                    <div
                                        class="rate-circle"
                                        :style="{ '--approval-rate': approvalRate + '%' }"
                                    >
                                        <div class="rate-value">{{ approvalRate }}%</div>
                                        <div class="rate-label">Aprovação</div>
                                    </div>

                                    <div class="rate-details">
                                        <div class="rate-item approved">
                                            <div class="rate-indicator"></div>
                                            <div class="rate-info">
                                                <span class="rate-count">{{ metrics.total_rides_approved || 0 }}</span>
                                                <span class="rate-text">Aprovadas</span>
                                                <span class="rate-percentage">{{ approvedPercentage }}%</span>
                                            </div>
                                        </div>
                                        <div class="rate-item rejected">
                                            <div class="rate-indicator"></div>
                                            <div class="rate-info">
                                                <span class="rate-count">{{ metrics.total_rides_rejected || 0 }}</span>
                                                <span class="rate-text">Reprovadas</span>
                                                <span class="rate-percentage">{{ rejectedPercentage }}%</span>
                                            </div>
                                        </div>
                                        <div class="rate-item pending">
                                            <div class="rate-indicator"></div>
                                            <div class="rate-info">
                                                <span class="rate-count">{{ metrics.total_rides_pending || 0 }}</span>
                                                <span class="rate-text">Pendentes</span>
                                                <span class="rate-percentage">{{ pendingPercentage }}%</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import http from "../../http";

export default {
    name: 'RideIndicators',
    data() {
        return {
            loading: true,
            error: null,
            metrics: {},
            filters: {
                branch_id: '',
                cost_center_id: '',
                start_date: '',
                end_date: ''
            },
            branches: [],
            costCenters: []
        }
    },
    computed: {
        approvalRate() {
            if (!this.metrics.total_rides_requested || this.metrics.total_rides_requested === 0) {
                return 0
            }
            return Math.round((this.metrics.total_rides_approved / this.metrics.total_rides_requested) * 100)
        },
        approvedPercentage() {
            if (!this.metrics.total_rides_requested || this.metrics.total_rides_requested === 0) {
                return 0
            }
            return Math.round((this.metrics.total_rides_approved / this.metrics.total_rides_requested) * 100)
        },
        rejectedPercentage() {
            if (!this.metrics.total_rides_requested || this.metrics.total_rides_requested === 0) {
                return 0
            }
            return Math.round((this.metrics.total_rides_rejected / this.metrics.total_rides_requested) * 100)
        },
        pendingPercentage() {
            if (!this.metrics.total_rides_requested || this.metrics.total_rides_requested === 0) {
                return 0
            }
            return Math.round((this.metrics.total_rides_pending / this.metrics.total_rides_requested) * 100)
        },
        efficiency() {
            return this.approvalRate
        },
        topCostCenter() {
            if (!this.metrics.top_sharing_cost_centers) return null

            const entries = Object.entries(this.metrics.top_sharing_cost_centers)
            if (entries.length === 0) return null

            const [name, count] = entries[0]
            return {name, count}
        }
    },
    mounted() {
        this.loadMetrics()
        this.loadBranches()
        this.loadCostCenters()
    },
    methods: {
        async loadMetrics() {
            try {
                this.loading = true
                this.error = null

                const api = this.$store.state.api + "dashboard/ride-metrics";
                const params = this.buildQueryParams()

                const response = await http.get(api, {params})

                if (response.data.success) {
                    this.metrics = response.data.data
                } else {
                    this.error = 'Erro ao carregar métricas de carona'
                }
            } catch (error) {
                console.error('Erro ao carregar métricas:', error)
                this.error = 'Erro ao conectar com o servidor'
            } finally {
                this.loading = false
            }
        },

        buildQueryParams() {
            const params = {}
            if (this.filters.branch_id) params.branch_id = this.filters.branch_id
            if (this.filters.cost_center_id) params.cost_center_id = this.filters.cost_center_id
            if (this.filters.start_date) params.start_date = this.filters.start_date
            if (this.filters.end_date) params.end_date = this.filters.end_date
            return params
        },

        async loadBranches() {
            try {
                const api = this.$store.state.api + "branches/?paginated=false";
                const response = await http.get(api)
                this.branches = response.data.data
            } catch (error) {
                console.error('Erro ao carregar filiais:', error)
            }
        },

        async loadCostCenters() {
            try {
                const api = this.$store.state.api + "cost-centers/?paginated=false";
                const response = await http.get(api)
                this.costCenters = [
                    {
                        id: '-1',
                        erp_code: "Veículos de locação"
                    },
                    ...response.data.data
                ]
            } catch (error) {
                console.error('Erro ao carregar centros de custo:', error)
            }
        },

        resetFilters() {
            this.filters = {
                branch_id: '',
                cost_center_id: '',
                start_date: '',
                end_date: ''
            }
            this.loadMetrics()
        },

        applyFilters() {
            this.loadMetrics()
        },

        calculatePercentage(count) {
            if (!this.metrics.top_sharing_cost_centers) return 0

            const maxCount = Math.max(...Object.values(this.metrics.top_sharing_cost_centers))
            return maxCount > 0 ? (count / maxCount) * 100 : 0
        }
    }
}
</script>

<style scoped>
.ride-indicators-page {
    padding: 0.75rem;
}

.metrics-header {
    text-align: center;
    margin-bottom: 2rem;
}

.metrics-title {
    color: #2c3e50;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.metrics-subtitle {
    color: #6c757d;
    margin: 0;
}

.loading-container {
    text-align: center;
    padding: 3rem;
}

.metrics-grid {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.main-metrics-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
}

@media (max-width: 1200px) {
    .main-metrics-row {
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 1rem;
    }
}

@media (max-width: 768px) {
    .main-metrics-row {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
}

.metric-card {
    background: white;
    border-radius: 12px;
    padding: 1.25rem 1rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e9ecef;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    gap: 1rem;
    position: relative;
    overflow: hidden;
    min-height: 90px;
}

.metric-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, var(--card-accent-color, #6c757d), transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
}

.metric-card:hover::before {
    opacity: 1;
}

.metric-card:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
}

.card-icon {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.3rem;
    color: white;
    flex-shrink: 0;
    transition: transform 0.3s ease;
}

.metric-card:hover .card-icon {
    transform: scale(1.1) rotate(5deg);
}

.rides-requested {
    --card-accent-color: #17a2b8;
}

.rides-requested .card-icon {
    background: linear-gradient(135deg, #17a2b8, #138496);
}

.rides-approved {
    --card-accent-color: #28a745;
}

.rides-approved .card-icon {
    background: linear-gradient(135deg, #28a745, #1e7e34);
}

.rides-rejected {
    --card-accent-color: #dc3545;
}

.rides-rejected .card-icon {
    background: linear-gradient(135deg, #dc3545, #c82333);
}

.rides-pending {
    --card-accent-color: #ffc107;
}

.rides-pending .card-icon {
    background: linear-gradient(135deg, #ffc107, #e0a800);
}

.shared-reservations {
    --card-accent-color: #6f42c1;
}

.shared-reservations .card-icon {
    background: linear-gradient(135deg, #6f42c1, #5a32a3);
}

.rides-automatic {
    --card-accent-color: #20c997;
}

.rides-automatic .card-icon {
    background: linear-gradient(135deg, #20c997, #17a589);
}

.card-content {
    flex: 1;
}

.card-value {
    font-size: 2rem;
    font-weight: 800;
    color: #2c3e50;
    line-height: 1;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2c3e50, #34495e);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 0.25rem;
}

.card-label {
    font-size: 0.85rem;
    color: #6c757d;
    font-weight: 500;
    line-height: 1.2;
    letter-spacing: 0.01em;
}

@media (max-width: 768px) {
    .card-value {
        font-size: 2rem;
    }

    .card-label {
        font-size: 0.85rem;
    }
}

.sharing-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    align-items: stretch;
}

@media (max-width: 1200px) {
    .sharing-section {
        grid-template-columns: 1fr;
        gap: 1.5rem;
    }
}

@media (max-width: 768px) {
    .sharing-section {
        gap: 1rem;
    }
}

.top-cost-centers-card {
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border: 1px solid #e9ecef;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    flex-direction: column;
    height: 100%;
}

.top-cost-centers-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.top-cost-centers-card .card-header {
    background: linear-gradient(135deg, #6f42c1, #5a32a3);
    color: white;
    padding: 1rem 1.25rem;
    border-radius: 12px 12px 0 0;
    border: none;
}

.top-cost-centers-card .card-header h5 {
    margin: 0;
    font-size: 1.1rem;
}

.top-cost-centers-card .card-body {
    padding: 1.25rem;
    flex: 1;
    display: flex;
    flex-direction: column;
}

.no-data {
    text-align: center;
    color: #6c757d;
    padding: 2rem;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
}

.cost-centers-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex: 1;
}

.cost-center-item {
    display: grid;
    grid-template-columns: 40px 1fr auto;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    background: #f8f9fa;
    border-radius: 6px;
    transition: all 0.2s ease;
}

.cost-center-item:hover {
    background: #e9ecef;
    transform: translateX(4px);
}

.first-place {
    background: linear-gradient(135deg, #fff3cd, #ffeaa7) !important;
    border-left: 4px solid #ffc107;
}

.second-place {
    background: linear-gradient(135deg, #f8f9fa, #e9ecef) !important;
    border-left: 4px solid #6c757d;
}

.third-place {
    background: linear-gradient(135deg, #fff5e6, #ffe4cc) !important;
    border-left: 4px solid #fd7e14;
}

.ranking-position {
    width: 40px;
    text-align: center;
    font-size: 1.2rem;
}

.position-number {
    font-weight: 600;
    color: #6c757d;
}

.cost-center-info {
    min-width: 0;
}

.cost-center-name {
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 0.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cost-center-count {
    font-size: 0.85rem;
    color: #6c757d;
}

.progress-bar {
    width: 60px;
    height: 6px;
    background: #e9ecef;
    border-radius: 3px;
    overflow: hidden;
    justify-self: end;
}

.progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #6f42c1, #5a32a3);
    transition: width 0.3s ease;
}

.approval-rate-card {
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border: 1px solid #e9ecef;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    flex-direction: column;
    height: 100%;
}

.approval-rate-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.approval-rate-header {
    background: linear-gradient(135deg, #28a745, #1e7e34);
    color: white;
    padding: 1rem 1.25rem;
    border-radius: 12px 12px 0 0;
}

.approval-rate-header h5 {
    margin: 0;
    font-size: 1.1rem;
}

.approval-rate-content {
    padding: 1.5rem;
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 2rem;
    flex: 1;
}

@media (max-width: 1024px) {
    .approval-rate-content {
        gap: 2rem;
        padding: 1.5rem;
    }
}

@media (max-width: 768px) {
    .approval-rate-content {
        grid-template-columns: 1fr;
        gap: 1.5rem;
        padding: 1rem;
        justify-items: center;
    }

    .rate-details {
        grid-template-columns: 1fr;
        gap: 0.5rem;
    }

    .rate-item {
        flex-direction: row;
        justify-content: flex-start;
        text-align: left;
    }

    .rate-info {
        align-items: flex-start;
    }

    .rate-circle {
        width: 100px;
        height: 100px;
    }

    .rate-circle::before {
        width: 70px;
        height: 70px;
    }

    .rate-value {
        font-size: 1.3rem;
    }
}

.rate-circle {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: conic-gradient(#28a745 0deg, #28a745 calc(var(--approval-rate) * 3.6deg), #e9ecef calc(var(--approval-rate) * 3.6deg));
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
}

.rate-circle::before {
    content: '';
    position: absolute;
    width: 70px;
    height: 70px;
    background: white;
    border-radius: 50%;
}

.rate-value,
.rate-label {
    position: relative;
    z-index: 1;
}

.rate-value {
    font-size: 1.3rem;
    font-weight: 700;
    color: #28a745;
    line-height: 1;
}

.rate-label {
    font-size: 0.7rem;
    color: #6c757d;
}

.rate-details {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 0.75rem;
    width: 100%;
}

.rate-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    text-align: center;
    justify-content: center;
    gap: 1rem;
    padding: 0.75rem 0.5rem;
    background: #f8f9fa;
    border-radius: 6px;
    transition: all 0.2s ease;
}

.rate-item:hover {
    background: #e9ecef;
    transform: translateX(2px);
}

.rate-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-bottom: 0.25rem;
}

.rate-info {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    align-items: center;
}

.rate-count {
    font-size: 1.1rem;
    font-weight: 700;
    color: #2c3e50;
    line-height: 1;
}

.rate-text {
    font-size: 0.75rem;
    color: #6c757d;
    font-weight: 500;
}

.rate-percentage {
    font-size: 0.8rem;
    font-weight: 600;
    color: #495057;
}

.rate-item.approved .rate-indicator {
    background: #28a745;
}

.rate-item.rejected .rate-indicator {
    background: #dc3545;
}

.rate-item.pending .rate-indicator {
    background: #ffc107;
}

/* Estilos dos filtros */
.metrics-header-card {
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border: 1px solid #e9ecef;
    margin-bottom: 2rem;
    overflow: hidden;
}

.metrics-header-card .card-header {
    background: linear-gradient(135deg, #6f42c1, #5a32a3);
    color: white;
    padding: 1.25rem;
    border: none;
}

.metrics-header-card .metrics-title {
    color: white;
    font-weight: 600;
}

.metrics-header-card .metrics-subtitle {
    color: rgba(255, 255, 255, 0.8);
}

.btn-action {
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    padding: 8px 16px;
    transition: all 0.2s ease;
    border: 1px solid rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.1);
    color: white;
}

.btn-action:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
    color: white;
    transform: translateY(-1px);
}

.filters-section {
    background-color: #f8f9fa;
    padding: 1.5rem;
    border-top: 1px solid #e9ecef;
}

.filters-section .form-group {
    margin-bottom: 1.5rem;
}

.filters-section label {
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
}

.d-flex.gap-2 {
    gap: 0.75rem;
}

/* Customização do v-select */
.style-chooser >>> .vs__dropdown-toggle {
    border: 1px solid #ced4da;
    border-radius: 8px;
    padding: 4px 8px;
    min-height: 42px;
    background: white;
}

.style-chooser >>> .vs__dropdown-toggle:hover {
    border-color: #6f42c1;
    box-shadow: 0 0 0 0.2rem rgba(111, 66, 193, 0.1);
}

.style-chooser >>> .vs__selected {
    color: #495057;
    font-weight: 500;
}

.style-chooser >>> .vs__search::placeholder {
    color: #6c757d;
}

.style-chooser >>> .vs__clear {
    margin-right: 8px;
}

.form-control {
    border-radius: 8px;
    border: 1px solid #ced4da;
    padding: 0.5rem 0.75rem;
    height: 42px;
}

.form-control:focus {
    border-color: #6f42c1;
    box-shadow: 0 0 0 0.2rem rgba(111, 66, 193, 0.1);
}

/* Seção de insights */
.insights-section {
    margin-top: 2rem;
}

.insights-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border: 1px solid #e9ecef;
    overflow: hidden;
}

.insights-header {
    background: linear-gradient(135deg, #17a2b8, #138496);
    color: white;
    padding: 1rem 1.5rem;
}

.insights-header h5 {
    margin: 0;
    font-size: 1.1rem;
}

.insights-body {
    padding: 2rem 1.5rem;
}

.insight-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 8px;
    border-left: 4px solid;
    transition: all 0.2s ease;
}

.insight-item:hover {
    background: #e9ecef;
    transform: translateX(4px);
}

.insight-item:nth-child(1) {
    border-left-color: #28a745;
}

.insight-item:nth-child(2) {
    border-left-color: #17a2b8;
}

.insight-item:nth-child(3) {
    border-left-color: #ffc107;
}

.insight-item:nth-child(4) {
    border-left-color: #6f42c1;
}

.insight-icon {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    color: white;
    flex-shrink: 0;
}

.insight-icon.sustainability {
    background: linear-gradient(135deg, #28a745, #20c997);
}

.insight-icon.efficiency {
    background: linear-gradient(135deg, #17a2b8, #138496);
}

.insight-icon.leadership {
    background: linear-gradient(135deg, #ffc107, #ffb300);
}

.insight-icon.collaboration {
    background: linear-gradient(135deg, #6f42c1, #5a32a3);
}

.insight-content h6 {
    margin-bottom: 0.5rem;
    color: #2c3e50;
    font-weight: 600;
}

.insight-content p {
    margin: 0;
    color: #6c757d;
    font-size: 0.9rem;
    line-height: 1.5;
}

@media (max-width: 768px) {
    .ride-indicators-page {
        padding: 0.5rem;
    }

    .insights-body {
        padding: 1rem;
    }

    .insight-item {
        flex-direction: column;
        text-align: center;
        gap: 0.5rem;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/Indicators/RideIndicators.vue